import React from 'react'
import {
  Alert,
  Box,
  Button,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  Skeleton,
  Snackbar,
  Stack,
  Tooltip,
  Typography,
  type PaperProps,
  type SxProps,
  type Theme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import StarIcon from '@mui/icons-material/Star'
import StarBorderIcon from '@mui/icons-material/StarBorder'
import AccessTimeIcon from '@mui/icons-material/AccessTimeRounded'
import { TransitionGroup } from 'react-transition-group'
import DashboardCard from './DashboardCard'
import PrepDashboardPlaceholderItem from './PrepDashboardPlaceholderItem'
import { type DashboardCardItemActions, type DashboardCardSettingsContext } from './types'
import {
  PREP_FETCH_RETRY_DELAYS_MS,
  buildNeutralChipSx,
  createItemTooltipSlotProps,
  fetchPrepItemDetailWithRetry,
  extractRequestId,
  isRetriablePrepFetchError,
  normalizePrepDashboardItems,
  type PrepDashboardNormalizedEntry,
  wait,
  withRequestId,
} from './utils'
import {
  addPrepItemToMyPrep,
  dismissForAll as dismissForAllApi,
  dismissPrepItem,
  getMyPrepList,
  removePrepItemFromMyPrep,
  undoDismissPrepItem,
  type PrepDashboardCard,
  type PrepListItem,
  type MyPrepListItemsResponse,
} from '../../../api/prep'
import usePrepListStore from '../../../store/prepListStore'
import usePrepUsageStore from '../../../store/prepUsageStore'
import useUserSettingsStore from '../../../store/userSettings'
import useStationStore from '../../../store/station'
import useShowClockPrepUsageStore from '../../../store/showClockPrepUsageStore'
import usePrepDashboardStore from '../../../store/prepDashboardStore'
import { syncAuthoritativeMyPrepOrder } from '../../../store/prepMembershipSync'
import {
  beginPrepUsedOptimistic,
  beginPrepUsedUndoOptimistic,
  commitPrepUsed,
  commitPrepUsedUndo,
  rollbackPrepUsed,
  rollbackPrepUsedUndo,
  type PrepUsedMutationSnapshot,
} from '../../../store/prepUsedSync'
import { useMe } from '../../../app/MeContext'
import { headlineClampSx } from '../styles'
import { resolveServerTotal } from '../../../utils/prepCounts'
import { coerceDetailToPrepListItem } from '../../../utils/prepItemNormalization'
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline'
import AddPrepToShowClockModal from '../AddPrepToShowClockModal'
import {
  capShowClockUsage,
  capShowClockUsageItems,
  formatShowClockUsageItemLabel,
  formatShowClockUsageLabel,
} from '../../../utils/showClockUsageLabels'

const REFRESH_INTERVAL_MS = 10 * 60 * 1000

type SnackbarAction = 'undo-dismiss' | 'undo-used'

type SnackbarState = {
  message: string
  severity: 'success' | 'error' | 'info'
  action?: SnackbarAction
}

interface MyPrepCardProps {
  card: PrepDashboardCard
  onCardChange: (next: PrepDashboardCard) => void
  onRegisterActions: (actions: DashboardCardItemActions | null) => void
  onSelectItem: (item: PrepListItem) => void
  isSuperadmin: boolean
  activeItemId?: number | null
  onActiveItemMissing?: () => void
  onItemsChange?: (items: PrepListItem[], total: number) => void
  onOpenSettings?: (context: DashboardCardSettingsContext) => void
  onRefreshCategories?: (categoryIds: number[]) => void
  snapshotData?: {
    items: PrepListItem[]
    total: number
    loading: boolean
    refreshing: boolean
    error: string | null
    version: number
  }
  onRequestMyPrepRefresh?: (expected?: { itemId: number; inMyPrep: boolean }) => Promise<void>
}

type DismissSnapshot = {
  item: PrepListItem
  wasInMyPrep: boolean
}

type UsedSnapshot = {
  mutation: PrepUsedMutationSnapshot
  previousItems: PrepListItem[]
  previousEntries: PrepDashboardNormalizedEntry[]
  previousTotal: number
}

const collectCategoryIds = (item: PrepListItem): number[] => {
  const ids = new Set<number>()
  if (Array.isArray(item.categories)) {
    for (const cat of item.categories) {
      if (cat && typeof cat.id === 'number' && Number.isFinite(cat.id) && cat.id > 0) {
        ids.add(cat.id)
      }
    }
  }
  if (item.primary_category_id && Number.isFinite(item.primary_category_id) && item.primary_category_id > 0) {
    ids.add(item.primary_category_id)
  }
  return Array.from(ids)
}

const MyPrepCard: React.FC<MyPrepCardProps> = ({
  card,
  onCardChange,
  onRegisterActions,
  onSelectItem,
  isSuperadmin,
  activeItemId,
  onActiveItemMissing,
  onItemsChange,
  onOpenSettings,
  onRefreshCategories,
  snapshotData,
  onRequestMyPrepRefresh,
}) => {
  const me = useMe()
  const usesSnapshot = snapshotData !== undefined
  const stationId = useStationStore((state) => state.activeStation?.id ?? 0)
  const updateMyItemInStore = usePrepListStore((state) => state.updateMyItem)
  const incMyCount = usePrepListStore((state) => state.incMyCount)
  const decMyCount = usePrepListStore((state) => state.decMyCount)
  const incMyCategoryCounts = usePrepListStore((state) => state.incMyCategoryCounts)
  const decMyCategoryCounts = usePrepListStore((state) => state.decMyCategoryCounts)
  const setMyCount = usePrepListStore((state) => state.setMyCount)
  const syncMyIdsFromItems = usePrepListStore((state) => state.syncMyIdsFromItems)
  const myIdsSet = usePrepListStore((state) => state.myIds)
  const myMembershipVersion = usePrepListStore((state) => state.myMembershipVersion)
  const globalHeadlineLines = useUserSettingsStore((state) => state.prep.headlineLines)
  const globalListShowImages = useUserSettingsStore((state) => state.prep.listShowImages)
  const headlineLines = card.headlineLines ?? globalHeadlineLines
  const listShowImages = card.listShowImages ?? globalListShowImages

  const [entries, setEntries] = React.useState<PrepDashboardNormalizedEntry[]>([])
  const [total, setTotal] = React.useState<number>(0)
  const [loading, setLoading] = React.useState<boolean>(true)
  const [refreshing, setRefreshing] = React.useState<boolean>(false)
  const [error, setError] = React.useState<string | null>(null)
  const [updatingIds, setUpdatingIds] = React.useState<number[]>([])
  const [busyIds, setBusyIds] = React.useState<number[]>([])
  const [placeholderRefreshingIds, setPlaceholderRefreshingIds] = React.useState<number[]>([])
  const [snackbar, setSnackbar] = React.useState<SnackbarState | null>(null)
  const itemTooltipSlotProps = React.useMemo(() => createItemTooltipSlotProps(), [])
  const usageStore = usePrepUsageStore()
  const lastUsedRef = React.useRef<UsedSnapshot | null>(null)
  const ensureShowClockUsage = useShowClockPrepUsageStore((state) => state.ensureUsage)
  const clocksByPrepId = useShowClockPrepUsageStore((state) => state.clocksByPrepId)
  const [addToShowClockTarget, setAddToShowClockTarget] = React.useState<PrepListItem | null>(null)

  React.useEffect(() => {
    if (usesSnapshot || !stationId || stationId <= 0 || entries.length === 0) {
      return
    }
    const ids = entries
      .map((entry) => entry.item?.id ?? null)
      .filter((id): id is number => typeof id === 'number' && id > 0)
    if (ids.length === 0) {
      return
    }
    void ensureShowClockUsage(stationId, ids)
  }, [ensureShowClockUsage, entries, stationId, usesSnapshot])

  const headerTitle = React.useMemo(
    () => {
      const handleClick = () => {
        usePrepListStore.getState().enterMyOnly?.()
      }

      return (
        <Chip
          size="small"
          component="button"
          tabIndex={0}
          label="My Prep Items"
          onClick={handleClick}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              handleClick()
            }
          }}
          sx={[
            buildNeutralChipSx(),
            {
              cursor: 'pointer',
              '&:focus-visible': (theme) => ({
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 2,
              }),
            },
          ]}
        />
      )
    },
    [],
  )

  const headerAccentSx = React.useMemo<SxProps<Theme>>(
    () =>
      ({
        '& > header': (theme) => ({
          backgroundColor: 'rgb(87, 87, 87)',
          color: theme.palette.common.white,
          '.MuiIconButton-root': {
            color: theme.palette.common.white,
            '&:hover, &.Mui-focusVisible': {
              backgroundColor: alpha(theme.palette.common.white, 0.16),
            },
          },
        }),
      }) satisfies NonNullable<PaperProps['sx']>,
    [],
  )

  const handleOpenSettings = React.useCallback(() => {
    if (!onOpenSettings) {
      return
    }
    onOpenSettings({
      cardId: card.id,
      type: 'my_prep',
      name: 'My Prep Items',
      slug: null,
    })
  }, [card.id, onOpenSettings])

  const cardRef = React.useRef(card)
  const mountedRef = React.useRef(false)
  const itemsRef = React.useRef<PrepListItem[]>([])
  const entriesRef = React.useRef<PrepDashboardNormalizedEntry[]>([])
  const totalRef = React.useRef(0)
  const highlightTimeoutRef = React.useRef<number | null>(null)
  const refreshTimerRef = React.useRef<number | null>(null)
  const scheduleRefreshRef = React.useRef<(() => void) | null>(null)
  const activeRequestRef = React.useRef<AbortController | null>(null)
  const fetchSequenceRef = React.useRef(0)
  const lastDismissedRef = React.useRef<DismissSnapshot | null>(null)
  const initialLoadRef = React.useRef(false)
  const initialFetchPendingRef = React.useRef(true)
  const onItemsChangeRef = React.useRef<typeof onItemsChange | undefined>(onItemsChange)

  React.useEffect(() => {
    onItemsChangeRef.current = onItemsChange
  }, [onItemsChange])

  React.useEffect(() => {
    if (!usesSnapshot) {
      void usePrepListStore.getState().ensureFullMyIds().catch(() => undefined)
    }
  }, [usesSnapshot])

  React.useLayoutEffect(() => {
    if (!snapshotData) {
      return
    }

    const normalizedEntries = normalizePrepDashboardItems(snapshotData.items).map((entry) =>
      entry.kind === 'item'
        ? { kind: 'item' as const, item: { ...entry.item, is_in_my_prep: true } }
        : entry,
    )
    const normalizedItems = normalizedEntries.flatMap((entry) =>
      entry.kind === 'item' ? [entry.item] : [],
    )
    entriesRef.current = normalizedEntries
    itemsRef.current = normalizedItems
    totalRef.current = Math.max(0, snapshotData.total)
    setEntries(normalizedEntries)
    setTotal(totalRef.current)
    setLoading(snapshotData.loading)
    setRefreshing(snapshotData.refreshing)
    setError(snapshotData.error)
    setMyCount(totalRef.current)
    onItemsChangeRef.current?.(normalizedItems, totalRef.current)
  }, [
    snapshotData?.error,
    snapshotData?.items,
    snapshotData?.loading,
    snapshotData?.refreshing,
    snapshotData?.total,
    snapshotData?.version,
    setMyCount,
  ])

  React.useEffect(() => {
    cardRef.current = card
  }, [card])

  React.useEffect(() => {
    return () => {
      if (activeRequestRef.current) {
        activeRequestRef.current.abort()
        activeRequestRef.current = null
      }
      if (typeof window !== 'undefined') {
        if (refreshTimerRef.current) {
          window.clearTimeout(refreshTimerRef.current)
          refreshTimerRef.current = null
        }
        if (highlightTimeoutRef.current) {
          window.clearTimeout(highlightTimeoutRef.current)
          highlightTimeoutRef.current = null
        }
      }
    }
  }, [])

  const markBusy = React.useCallback((id: number) => {
    setBusyIds((prev) => (prev.includes(id) ? prev : [...prev, id]))
  }, [])

  const clearBusy = React.useCallback((id: number) => {
    setBusyIds((prev) => prev.filter((value) => value !== id))
  }, [])

  const rollbackRemoval = React.useCallback(
    ({
      previousItems,
      previousEntries,
      previousTotal,
    }: {
      previousItems: PrepListItem[]
      previousEntries: PrepDashboardNormalizedEntry[]
      previousTotal: number
    }) => {
      itemsRef.current = previousItems
      entriesRef.current = previousEntries
      setEntries(previousEntries)
      totalRef.current = previousTotal
      setTotal(previousTotal)
      onItemsChangeRef.current?.(previousItems, previousTotal)
    },
    [onItemsChangeRef],
  )

  const optimisticRemove = React.useCallback(
    (item: PrepListItem): {
      previousItems: PrepListItem[]
      previousEntries: PrepDashboardNormalizedEntry[]
      previousTotal: number
      removed: boolean
      nextTotal: number
    } => {
      const previousItems = itemsRef.current
      const previousEntries = entriesRef.current
      const previousTotal = totalRef.current
      const itemIndex = previousItems.findIndex((entry) => entry.id === item.id)
      if (itemIndex === -1) {
        return {
          previousItems,
          previousEntries,
          previousTotal,
          removed: false,
          nextTotal: previousTotal,
        }
      }

      const entryIndex = previousEntries.findIndex(
        (entry) => entry.kind === 'item' && entry.item.id === item.id,
      )
      const nextEntries =
        entryIndex === -1
          ? previousEntries
          : [
              ...previousEntries.slice(0, entryIndex),
              ...previousEntries.slice(entryIndex + 1),
            ]
      const nextItems = [
        ...previousItems.slice(0, itemIndex),
        ...previousItems.slice(itemIndex + 1),
      ]

      entriesRef.current = nextEntries
      itemsRef.current = nextItems
      setEntries(nextEntries)

      const nextTotal = Math.max(0, previousTotal - 1)
      totalRef.current = nextTotal
      setTotal(nextTotal)
      onItemsChangeRef.current?.(nextItems, nextTotal)

      return {
        previousItems,
        previousEntries,
        previousTotal,
        removed: true,
        nextTotal,
      }
    },
    [onItemsChangeRef],
  )

  const fetchItems = React.useCallback(
    async ({
      background = false,
      expectedMembership,
    }: {
      background?: boolean
      expectedMembership?: { itemId: number; inMyPrep: boolean }
    } = {}) => {
      if (usesSnapshot) {
        await onRequestMyPrepRefresh?.(expectedMembership)
        return
      }

      const sequenceId = fetchSequenceRef.current + 1
      fetchSequenceRef.current = sequenceId

      if (activeRequestRef.current) {
        activeRequestRef.current.abort()
        activeRequestRef.current = null
      }

      if (background) {
        setRefreshing(true)
      } else {
        setLoading(true)
        setRefreshing(false)
        setError(null)
      }

      const attemptCount = PREP_FETCH_RETRY_DELAYS_MS.length + 1
      let finalError: unknown = null
      let succeeded = false
      let aborted = false

      for (let attemptIndex = 0; attemptIndex < attemptCount; attemptIndex += 1) {
        if (fetchSequenceRef.current !== sequenceId) {
          aborted = true
          break
        }

        if (typeof window !== 'undefined' && refreshTimerRef.current) {
          window.clearTimeout(refreshTimerRef.current)
          refreshTimerRef.current = null
        }

        const controller = new AbortController()
        activeRequestRef.current = controller

        try {
          const response = (await getMyPrepList({
            page: cardRef.current.page,
            limit: cardRef.current.itemsPerPage,
            idsOnly: false,
            signal: controller.signal,
          })) as MyPrepListItemsResponse

          if (controller.signal.aborted || fetchSequenceRef.current !== sequenceId) {
            aborted = true
            break
          }

          const normalizedEntries = normalizePrepDashboardItems(response.items).map(
            (entry) =>
              entry.kind === 'item'
                ? { kind: 'item', item: { ...entry.item, is_in_my_prep: true } }
                : entry,
          )
          const normalizedItems = normalizedEntries.flatMap((entry) =>
            entry.kind === 'item' ? [entry.item] : [],
          )
          const previousItems = itemsRef.current
          const previousMap = new Map(previousItems.map((item) => [item.id, item]))
          const changedIds: number[] = []

          normalizedItems.forEach((item) => {
            const previous = previousMap.get(item.id)
            if (!previous) {
              changedIds.push(item.id)
              return
            }
            if (
              previous.headline !== item.headline ||
              previous.short_preview !== item.short_preview ||
              previous.tease !== item.tease ||
              previous.is_in_my_prep !== item.is_in_my_prep ||
              previous.published_at !== item.published_at
            ) {
              changedIds.push(item.id)
            }
          })

          itemsRef.current = normalizedItems
          entriesRef.current = normalizedEntries
          setEntries(normalizedEntries)
          totalRef.current = resolveServerTotal(response, normalizedEntries)
          setTotal(totalRef.current)
          onItemsChangeRef.current?.(normalizedItems, totalRef.current)
          setError(null)

          if (initialLoadRef.current && changedIds.length > 0) {
            setUpdatingIds(changedIds)
            if (typeof window !== 'undefined') {
              if (highlightTimeoutRef.current) {
                window.clearTimeout(highlightTimeoutRef.current)
              }
              highlightTimeoutRef.current = window.setTimeout(() => {
                setUpdatingIds([])
                highlightTimeoutRef.current = null
              }, 600)
            }
          }

          if (!initialLoadRef.current) {
            initialLoadRef.current = true
          }

          syncMyIdsFromItems(normalizedItems)
          setMyCount(totalRef.current)
          succeeded = true
          finalError = null
          break
        } catch (error) {
          if (controller.signal.aborted || fetchSequenceRef.current !== sequenceId) {
            aborted = true
            break
          }

          finalError = error

          const hasMoreAttempts = attemptIndex < attemptCount - 1
          if (!hasMoreAttempts || !isRetriablePrepFetchError(error)) {
            break
          }

          const delayMs = PREP_FETCH_RETRY_DELAYS_MS[attemptIndex] ?? 0
          if (fetchSequenceRef.current !== sequenceId) {
            aborted = true
            break
          }
          if (delayMs > 0) {
            await wait(delayMs)
          }
        } finally {
          if (activeRequestRef.current === controller) {
            activeRequestRef.current = null
          }
        }
      }

      if (aborted || fetchSequenceRef.current !== sequenceId) {
        if (fetchSequenceRef.current === sequenceId) {
          setLoading(false)
          setRefreshing(false)
        }
        return
      }

      if (!succeeded && finalError) {
        console.error('MyPrepCard fetch failed', finalError)
        const requestId = extractRequestId(finalError)
        const message =
          finalError && typeof finalError === 'object' && 'message' in finalError
            ? String((finalError as { message?: unknown }).message ?? 'Unable to load My Prep items.')
            : 'Unable to load My Prep items.'
        const display = withRequestId(message, requestId)
        setError(display)
        if (!background) {
          setSnackbar({ message: display, severity: 'error' })
        }
      }

      setLoading(false)
      setRefreshing(false)

      if (initialFetchPendingRef.current) {
        initialFetchPendingRef.current = false
      }

      scheduleRefreshRef.current?.()
    },
    [onRequestMyPrepRefresh, setMyCount, syncMyIdsFromItems, usesSnapshot],
  )

  const handleRefreshPlaceholder = React.useCallback(
    async (id: number) => {
      setPlaceholderRefreshingIds((previous) =>
        previous.includes(id) ? previous : [...previous, id],
      )

      try {
        const entryIndexBefore = entriesRef.current.findIndex(
          (entry) => entry.kind === 'placeholder' && entry.id === id,
        )

        if (entryIndexBefore === -1) {
          return
        }

        const detail = await fetchPrepItemDetailWithRetry(id)
        const normalizedDetail = coerceDetailToPrepListItem(detail, { fallbackInMyPrep: true })
        const normalizedItem: PrepListItem = { ...normalizedDetail, is_in_my_prep: true }

        const currentEntries = entriesRef.current
        const placeholderIndex = currentEntries.findIndex(
          (entry) => entry.kind === 'placeholder' && entry.id === id,
        )

        if (placeholderIndex === -1) {
          return
        }

        const nextEntries: PrepDashboardNormalizedEntry[] = [...currentEntries]
        nextEntries[placeholderIndex] = { kind: 'item', item: normalizedItem }

        entriesRef.current = nextEntries
        setEntries(nextEntries)

        const nextItems = nextEntries.flatMap((entry) =>
          entry.kind === 'item' ? [entry.item] : [],
        )
        itemsRef.current = nextItems

        onItemsChangeRef.current?.(nextItems, totalRef.current)
        syncMyIdsFromItems(nextItems)

        void fetchItems({ background: true })
      } catch (error) {
        console.error('Unable to refresh prep placeholder', error)
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to refresh prep item.')
            : 'Unable to refresh prep item.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        setPlaceholderRefreshingIds((previous) => previous.filter((value) => value !== id))
      }
    },
    [fetchItems, onItemsChangeRef, setSnackbar, syncMyIdsFromItems],
  )

  const updateCard = React.useCallback(
    (mutator: (current: PrepDashboardCard) => PrepDashboardCard) => {
      const next = mutator(cardRef.current)
      onCardChange(next)
    },
    [onCardChange],
  )

  const adjustAfterRemoval = React.useCallback(
    (nextTotal: number, expectedItemId?: number) => {
      const current = cardRef.current
      const perPageValue = Math.max(1, current.itemsPerPage || 1)
      const maxPage = Math.max(1, Math.ceil(Math.max(0, nextTotal) / perPageValue))
      if (current.page > maxPage) {
        updateCard((state) => ({ ...state, page: maxPage }))
        return
      }
      void fetchItems({
        background: true,
        ...(expectedItemId
          ? { expectedMembership: { itemId: expectedItemId, inMyPrep: false } }
          : {}),
      })
    },
    [fetchItems, updateCard],
  )

  const toggleMyPrepForItem = React.useCallback(
    async (item: PrepListItem, next: boolean) => {
      markBusy(item.id)
      const storeState = usePrepListStore.getState()
      const previousInMyPrep = Boolean(item.is_in_my_prep || storeState.myIds.has(item.id))
      const previousOrder = [...storeState.myAllOrderedIds]
      if (!next) {
        const { previousItems, previousEntries, previousTotal, removed, nextTotal } =
          optimisticRemove(item)
        if (!removed) {
          clearBusy(item.id)
          return
        }

        updateMyItemInStore(item, false)
        const optimisticOrder = [...usePrepListStore.getState().myAllOrderedIds]
        usePrepDashboardStore.getState().patchMyPrepMembership(
          item.id,
          false,
          item,
          optimisticOrder,
        )
        if (previousInMyPrep) {
          decMyCount()
          decMyCategoryCounts(item)
        }

        try {
          const response = await removePrepItemFromMyPrep(item.id)
          syncAuthoritativeMyPrepOrder(response, optimisticOrder)
          setSnackbar({ message: 'Removed from My Prep', severity: 'success' })
          adjustAfterRemoval(nextTotal, item.id)
        } catch (error) {
          rollbackRemoval({ previousItems, previousEntries, previousTotal })
          updateMyItemInStore(item, true)
          syncAuthoritativeMyPrepOrder(null, previousOrder)
          if (previousInMyPrep) {
            incMyCount()
            incMyCategoryCounts(item)
          }
          const requestId = extractRequestId(error)
          const message =
            error && typeof error === 'object' && 'message' in error
              ? String((error as { message?: unknown }).message ?? 'Unable to remove from My Prep.')
              : 'Unable to remove from My Prep.'
          setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
        } finally {
          clearBusy(item.id)
        }
        return
      }

      updateMyItemInStore(item, true)
      const optimisticOrder = [...usePrepListStore.getState().myAllOrderedIds]
      usePrepDashboardStore.getState().patchMyPrepMembership(
        item.id,
        true,
        item,
        optimisticOrder,
      )
      if (!previousInMyPrep) {
        incMyCount()
        incMyCategoryCounts(item)
      }
      try {
        const response = await addPrepItemToMyPrep(item.id)
        syncAuthoritativeMyPrepOrder(response, optimisticOrder)
        setSnackbar({ message: 'Added to My Prep', severity: 'success' })
        void fetchItems({
          background: true,
          expectedMembership: { itemId: item.id, inMyPrep: true },
        })
      } catch (error) {
        updateMyItemInStore(item, false)
        syncAuthoritativeMyPrepOrder(null, previousOrder)
        if (!previousInMyPrep) {
          decMyCount()
          decMyCategoryCounts(item)
        }
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to add to My Prep.')
            : 'Unable to add to My Prep.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        clearBusy(item.id)
      }
    },
    [
      adjustAfterRemoval,
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      fetchItems,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      optimisticRemove,
      rollbackRemoval,
      updateMyItemInStore,
    ],
  )

  const dismissForMe = React.useCallback(
    async (item: PrepListItem) => {
      markBusy(item.id)
        const { previousItems, previousEntries, previousTotal, removed, nextTotal } =
          optimisticRemove(item)
      if (!removed) {
        clearBusy(item.id)
        return
      }

      const storeState = usePrepListStore.getState()
      const wasInMyPrep = Boolean(item.is_in_my_prep || storeState.myIds.has(item.id))
      if (wasInMyPrep) {
        updateMyItemInStore(item, false)
        usePrepDashboardStore.getState().patchMyPrepMembership(item.id, false)
        decMyCount()
        decMyCategoryCounts(item)
        storeState.bumpMyMembershipVersion()
      }

      lastDismissedRef.current = { item, wasInMyPrep }

      try {
        await dismissPrepItem(item.id)
        setSnackbar({ message: 'Dismissed for you.', severity: 'success', action: 'undo-dismiss' })
        adjustAfterRemoval(nextTotal)
      } catch (error) {
        rollbackRemoval({ previousItems, previousEntries, previousTotal })
        if (wasInMyPrep) {
          updateMyItemInStore(item, true)
          usePrepDashboardStore.getState().patchMyPrepMembership(item.id, true)
          incMyCount()
          incMyCategoryCounts(item)
          usePrepListStore.getState().bumpMyMembershipVersion()
        }
        lastDismissedRef.current = null
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to dismiss prep item.')
            : 'Unable to dismiss prep item.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        clearBusy(item.id)
      }
    },
    [
      adjustAfterRemoval,
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      optimisticRemove,
      rollbackRemoval,
      updateMyItemInStore,
    ],
  )

  const markUsedForMe = React.useCallback(
    async (item: PrepListItem) => {
      if (!stationId) {
        setSnackbar({ message: 'No station selected.', severity: 'error' })
        return
      }
      markBusy(item.id)
      const { removed, nextTotal, previousEntries, previousItems, previousTotal } = optimisticRemove(item)
      if (!removed) {
        clearBusy(item.id)
        return
      }

      const mutation = beginPrepUsedOptimistic({
        item,
        userId: me.user.id,
        userName: me.user.name,
      })

      if (!mutation.owner) {
        clearBusy(item.id)
        return
      }

      if (mutation.wasInMyPrep) {
        decMyCategoryCounts(item)
      }

      lastUsedRef.current = {
        mutation,
        previousEntries,
        previousItems,
        previousTotal,
      }

      try {
        const response = await usageStore.markUsed(stationId, item.id)
        if (!response) {
          throw new Error('Unable to mark as used.')
        }
        if (!commitPrepUsed(mutation, response)) {
          return
        }
        setSnackbar({ message: 'Marked as used.', severity: 'success', action: 'undo-used' })
        const categoryIds = collectCategoryIds(item)
        if (categoryIds.length > 0 && onRefreshCategories) {
          onRefreshCategories(categoryIds)
        }
        adjustAfterRemoval(nextTotal)
      } catch (error) {
        if (!rollbackPrepUsed(mutation)) {
          return
        }
        rollbackRemoval({ previousItems, previousEntries, previousTotal })
        if (mutation.wasInMyPrep) {
          incMyCategoryCounts(item)
        }
        lastUsedRef.current = null
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to mark as used.')
            : 'Unable to mark as used.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
        throw error
      } finally {
        clearBusy(item.id)
      }
    },
    [
      adjustAfterRemoval,
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      me.user.id,
      me.user.name,
      optimisticRemove,
      rollbackRemoval,
      stationId,
      usageStore,
    ],
  )

  const dismissForAllHandler = React.useCallback(
    async (item: PrepListItem) => {
      markBusy(item.id)
        const { previousItems, previousEntries, previousTotal, removed, nextTotal } =
          optimisticRemove(item)
      if (!removed) {
        clearBusy(item.id)
        return
      }

      const storeState = usePrepListStore.getState()
      const wasInMyPrep = Boolean(item.is_in_my_prep || storeState.myIds.has(item.id))
      if (wasInMyPrep) {
        updateMyItemInStore(item, false)
        usePrepDashboardStore.getState().patchMyPrepMembership(item.id, false)
        decMyCount()
        decMyCategoryCounts(item)
        storeState.bumpMyMembershipVersion()
      }

      try {
        await dismissForAllApi(item.id)
        setSnackbar({ message: 'Dismissed for everyone.', severity: 'success' })
        adjustAfterRemoval(nextTotal)
      } catch (error) {
        rollbackRemoval({ previousItems, previousEntries, previousTotal })
        if (wasInMyPrep) {
          updateMyItemInStore(item, true)
          usePrepDashboardStore.getState().patchMyPrepMembership(item.id, true)
          incMyCount()
          incMyCategoryCounts(item)
          usePrepListStore.getState().bumpMyMembershipVersion()
        }
        const requestId = extractRequestId(error)
        const message =
          error && typeof error === 'object' && 'message' in error
            ? String((error as { message?: unknown }).message ?? 'Unable to dismiss for everyone.')
            : 'Unable to dismiss for everyone.'
        setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
      } finally {
        clearBusy(item.id)
      }
    },
    [
      adjustAfterRemoval,
      clearBusy,
      decMyCategoryCounts,
      decMyCount,
      incMyCategoryCounts,
      incMyCount,
      markBusy,
      optimisticRemove,
      rollbackRemoval,
      updateMyItemInStore,
    ],
  )

  const handleUndoDismiss = React.useCallback(async () => {
    const snapshot = lastDismissedRef.current
    if (!snapshot) {
      return
    }

    setSnackbar(null)

    try {
      await undoDismissPrepItem(snapshot.item.id)
      if (snapshot.wasInMyPrep) {
        updateMyItemInStore(snapshot.item, true)
        incMyCount()
        incMyCategoryCounts(snapshot.item)
        void addPrepItemToMyPrep(snapshot.item.id).catch((error) => {
          if (process.env.NODE_ENV !== 'production') {
            console.error('[prep] Failed to restore My Prep after undo dismiss', error)
          }
        })
      }
      setSnackbar({ message: 'Restored to your feed.', severity: 'success' })
      void fetchItems({ background: true })
    } catch (error) {
      const requestId = extractRequestId(error)
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message ?? 'Unable to restore prep item.')
          : 'Unable to restore prep item.'
      setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
    } finally {
      lastDismissedRef.current = null
    }
  }, [fetchItems, incMyCategoryCounts, incMyCount, updateMyItemInStore])

  const handleUndoUsed = React.useCallback(async () => {
    const snapshot = lastUsedRef.current
    if (!snapshot || !stationId) {
      return
    }

    setSnackbar(null)

    try {
      rollbackRemoval({
        previousItems: snapshot.previousItems,
        previousEntries: snapshot.previousEntries,
        previousTotal: snapshot.previousTotal,
      })
      beginPrepUsedUndoOptimistic(snapshot.mutation)
      if (snapshot.mutation.wasInMyPrep) {
        incMyCategoryCounts(snapshot.mutation.item)
      }

      const response = await usageStore.undoUsed(
        stationId,
        snapshot.mutation.item.id,
        undefined,
        {
          myPrep: snapshot.mutation.wasInMyPrep,
          position: snapshot.mutation.previousMyPrepPosition,
        },
      )
      if (!response?.undone) {
        throw new Error('Unable to undo used flag.')
      }
      if (!commitPrepUsedUndo(snapshot.mutation, response)) {
        return
      }
      setSnackbar({ message: 'Marked as unused and restored.', severity: 'success' })
      void fetchItems({ background: true })
      if (onRefreshCategories) {
        const categoryIds = collectCategoryIds(snapshot.mutation.item)
        if (categoryIds.length > 0) {
          onRefreshCategories(categoryIds)
        }
      }
    } catch (error) {
      if (!rollbackPrepUsedUndo(snapshot.mutation)) {
        return
      }
      optimisticRemove(snapshot.mutation.item)
      if (snapshot.mutation.wasInMyPrep) {
        decMyCategoryCounts(snapshot.mutation.item)
      }
      const requestId = extractRequestId(error)
      const message =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message?: unknown }).message ?? 'Unable to undo used flag.')
          : 'Unable to undo used flag.'
      setSnackbar({ message: withRequestId(message, requestId), severity: 'error' })
    } finally {
      lastUsedRef.current = null
    }
  }, [
    decMyCategoryCounts,
    decMyCount,
    fetchItems,
    incMyCategoryCounts,
    incMyCount,
    onRefreshCategories,
    optimisticRemove,
    rollbackRemoval,
    stationId,
    usageStore,
  ])

  const handleSnackbarClose = (_event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return
    }
    setSnackbar(null)
  }

  React.useEffect(() => {
    mountedRef.current = true
    return () => {
      mountedRef.current = false
    }
  }, [])

  React.useEffect(() => {
    if (usesSnapshot) {
      return
    }
    void fetchItems()
  }, [card.page, card.itemsPerPage, fetchItems, usesSnapshot])

  React.useEffect(() => {
    if (usesSnapshot || !mountedRef.current || initialFetchPendingRef.current) {
      return
    }
    void fetchItems({ background: true })
  }, [myMembershipVersion, fetchItems, usesSnapshot])

  const scheduleRefresh = React.useCallback(() => {
    if (usesSnapshot || typeof window === 'undefined') {
      return
    }
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current)
    }
    refreshTimerRef.current = window.setTimeout(() => {
      refreshTimerRef.current = null
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        scheduleRefresh()
        return
      }
      void fetchItems({ background: true })
      scheduleRefresh()
    }, REFRESH_INTERVAL_MS)
  }, [fetchItems, usesSnapshot])

  React.useEffect(() => {
    scheduleRefreshRef.current = scheduleRefresh
    return () => {
      if (scheduleRefreshRef.current === scheduleRefresh) {
        scheduleRefreshRef.current = null
      }
    }
  }, [scheduleRefresh])

  React.useEffect(() => {
    if (usesSnapshot || typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined
    }

    scheduleRefresh()

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        void fetchItems({ background: true })
        scheduleRefresh()
      } else if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current)
        refreshTimerRef.current = null
      }
    }

    document.addEventListener('visibilitychange', handleVisibility)

    return () => {
      document.removeEventListener('visibilitychange', handleVisibility)
    }
  }, [fetchItems, scheduleRefresh, usesSnapshot])

  const actions = React.useMemo<DashboardCardItemActions>(
    () => ({
      getItemById: (id) => itemsRef.current.find((item) => item.id === id) ?? null,
      toggleMyPrep: async (itemId, next) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await toggleMyPrepForItem(item, next)
      },
      dismissForMe: async (itemId) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await dismissForMe(item)
      },
      markUsedForMe: async (itemId) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await markUsedForMe(item)
      },
      dismissForAll: async (itemId) => {
        const item = itemsRef.current.find((entry) => entry.id === itemId)
        if (!item) {
          return
        }
        await dismissForAllHandler(item)
      },
    }),
    [dismissForAllHandler, dismissForMe, markUsedForMe, toggleMyPrepForItem],
  )

  React.useEffect(() => {
    onRegisterActions(actions)
    return () => {
      onRegisterActions(null)
    }
  }, [actions, onRegisterActions])

  const handleSelectItem = (item: PrepListItem) => {
    onSelectItem(item)
  }

  const handleChangePage = (page: number) => {
    updateCard((current) => ({ ...current, page }))
  }

  const handleToggleCollapse = (collapsed: boolean) => {
    updateCard((current) => ({ ...current, collapsed }))
  }

  const perPage = Math.max(3, Math.min(15, Math.floor(card.itemsPerPage || 5)))
  const page = Math.max(1, Math.floor(card.page || 1))
  const collapsed = Boolean(card.collapsed)

  const hasRealItems = entries.some((entry) => entry.kind === 'item')
  const hasPlaceholders = entries.some((entry) => entry.kind === 'placeholder')
  const showEmptyState = !loading && !error && !hasRealItems && !hasPlaceholders

  React.useEffect(() => {
    if (!activeItemId) {
      return
    }
    const exists = itemsRef.current.some((entry) => entry.id === activeItemId)
    if (!exists) {
      onActiveItemMissing?.()
    }
  }, [activeItemId, onActiveItemMissing, entries])

  const snackbarAction =
    snackbar?.action === 'undo-dismiss' ? (
      <Button color="inherit" size="small" onClick={handleUndoDismiss}>
        Undo
      </Button>
    ) : snackbar?.action === 'undo-used' ? (
      <Button color="inherit" size="small" onClick={handleUndoUsed}>
        Undo
      </Button>
    ) : undefined

  let content: React.ReactNode

  if (loading) {
    const skeletonCount = Math.max(3, perPage)
    content = (
      <Stack spacing={1.25} sx={{ pt: 0.5 }}>
        {Array.from({ length: skeletonCount }).map((_, index) => (
          <Skeleton key={index} variant="rounded" height={28} animation="wave" />
        ))}
      </Stack>
    )
  } else if (error) {
    content = (
      <Alert severity="error" sx={{ mt: 0.5 }}>
        {error}
      </Alert>
    )
  } else if (showEmptyState) {
    content = (
      <Box sx={{ py: 3 }}>
        <Typography variant="body2" color="text.secondary">
          No saved prep items yet.
        </Typography>
      </Box>
    )
  } else {
    content = (
      <List disablePadding sx={{ minWidth: 0, overflowX: 'hidden' }}>
        <TransitionGroup component={null}>
          {entries.map((entry) => {
            if (entry.kind === 'placeholder') {
              const refreshingPlaceholder = placeholderRefreshingIds.includes(entry.id)
              return (
                <Collapse key={`placeholder-${entry.id}`} timeout={200}>
                  <ListItem
                    disablePadding
                    divider
                    sx={{
                      alignItems: 'center',
                      display: 'flex',
                      gap: 0.25,
                      px: 0,
                      minWidth: 0,
                    }}
                  >
                    <Box
                      sx={{
                        flex: '1 1 auto',
                        minWidth: 0,
                        py: 0.85,
                        px: 0.25,
                        borderRadius: 1,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <PrepDashboardPlaceholderItem
                        id={entry.id}
                        slotText={entry.placeholderText}
                        refreshing={refreshingPlaceholder}
                        onRefresh={() => {
                          void handleRefreshPlaceholder(entry.id)
                        }}
                      />
                    </Box>
                  </ListItem>
                </Collapse>
              )
            }

            const item = entry.item
            const isUpdating = updatingIds.includes(item.id)
            const isBusy = busyIds.includes(item.id)
            const headline = item.headline?.trim() || `Prep item #${item.id}`
            const previewTooltip = item.short_preview?.trim() || ''
            const tooltipTitle = headline
              ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                    <Typography
                      component="div"
                      sx={(theme) => ({
                        fontWeight: 600,
                        fontSize: theme.typography.pxToRem(15),
                        lineHeight: 1.4,
                        color: 'inherit',
                      })}
                    >
                      {headline}
                    </Typography>
                    {previewTooltip ? (
                      <Typography
                        component="div"
                        sx={(theme) => ({
                          mt: 0.5,
                          fontSize: theme.typography.pxToRem(14),
                          lineHeight: 1.5,
                          color: 'inherit',
                          whiteSpace: 'pre-line',
                        })}
                      >
                        {previewTooltip}
                      </Typography>
                    ) : null}
                  </Box>
                )
              : null
            const isStarred = item.is_in_my_prep || myIdsSet.has(item.id)
            const headlineNode = (
              <Typography
                variant="body2"
                sx={[{ fontWeight: 500 }, headlineClampSx(headlineLines)]}
              >
                {headline}
              </Typography>
            )
            const imageThumb =
              typeof item.image?.thumb === 'string' ? item.image.thumb.trim() : ''
            const imageAlt = typeof item.image?.alt === 'string' ? item.image.alt : ''
            const showThumbnail = listShowImages && imageThumb.length > 0

            const textContent = (
              <Box
                sx={[
                  { position: 'relative', width: '100%', minWidth: 0 },
                  showThumbnail
                    ? {
                        overflow: 'hidden',
                        pr: 6,
                        minHeight: 48,
                      }
                    : null,
                ]}
              >
                {tooltipTitle ? (
                  <Tooltip
                    title={tooltipTitle}
                    placement="top-start"
                    enterDelay={400}
                    describeChild
                    slotProps={itemTooltipSlotProps}
                  >
                    <Box component="span" sx={{ display: 'block', minWidth: 0 }}>{headlineNode}</Box>
                  </Tooltip>
                ) : (
                  headlineNode
                )}
                {showThumbnail ? (
                  <Box
                    component="img"
                    src={imageThumb}
                    alt={imageAlt}
                    sx={{
                      position: 'absolute',
                      top: 0,
                      right: 0,
                      width: 40,
                      height: 40,
                      borderRadius: '50%',
                      objectFit: 'cover',
                      boxShadow: (theme) => `0 0 0 1px ${theme.palette.divider}`,
                    }}
                    loading="lazy"
                  />
                ) : null}
                {isUpdating ? (
                  <Skeleton
                    variant="rectangular"
                    animation="wave"
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: 1,
                      opacity: 0.35,
                      pointerEvents: 'none',
                    }}
                  />
                ) : null}
              </Box>
            )

            const actionsNode = (
              <Box
                sx={{
                  flex: '0 0 auto',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.15,
                  '& .MuiIconButton-root': { p: 0.25 },
                }}
              >
                <Tooltip title={isStarred ? 'Remove from My Prep' : 'Add to My Prep'} enterDelay={300}>
                  <span>
                    <IconButton
                      aria-label={isStarred ? 'Remove from My Prep' : 'Add to My Prep'}
                      size="small"
                      onClick={() => void toggleMyPrepForItem(item, !isStarred)}
                      disabled={isBusy}
                    >
                      {isStarred ? <StarIcon fontSize="small" color="warning" /> : <StarBorderIcon fontSize="small" />}
                    </IconButton>
                  </span>
                </Tooltip>
                {(() => {
                  const showClockUsage = clocksByPrepId.get(item.id) ?? []
                  const inLiveOrFutureClock = showClockUsage.length > 0
                  const tooltipTitle = inLiveOrFutureClock ? (
                    (() => {
                      const { clocks, extraCount } = capShowClockUsage(showClockUsage, 3)
                      return (
                        <Box sx={{ py: 0.25 }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            In live/future show clocks
                          </Typography>
                          <Box component="div" sx={{ mt: 0.5 }}>
                            {clocks.map((clock, index) => {
                              const usageItems = Array.isArray(clock.items) ? clock.items : []
                              const { items, extraCount: extraItems } = capShowClockUsageItems(usageItems, 3)

                              return (
                                <Box key={clock.day_id} component="div" sx={{ mt: index === 0 ? 0 : 0.5 }}>
                                  <Box component="div" sx={{ typography: 'caption', fontWeight: 600 }}>
                                    {formatShowClockUsageLabel(clock)}
                                  </Box>
                                  {items.map((item) => (
                                    <Box key={item.day_item_id} component="div" sx={{ typography: 'caption', ml: 1 }}>
                                      {formatShowClockUsageItemLabel(item)}
                                    </Box>
                                  ))}
                                  {extraItems > 0 ? (
                                    <Box component="div" sx={{ typography: 'caption', ml: 1, mt: 0.25 }}>
                                      +{extraItems} more
                                    </Box>
                                  ) : null}
                                </Box>
                              )
                            })}
                            {extraCount > 0 ? (
                              <Box component="div" sx={{ typography: 'caption', mt: 0.25 }}>
                                +{extraCount} more
                              </Box>
                            ) : null}
                          </Box>
                        </Box>
                      )
                    })()
                  ) : (
                    'Add to Show Clock'
                  )

                  return (
                    <Tooltip title={tooltipTitle} enterDelay={300}>
                      <span>
                        <IconButton
                          aria-label="Add to Show Clock"
                          size="small"
                          onClick={() => setAddToShowClockTarget(item)}
                          disabled={isBusy || !stationId}
                          color={inLiveOrFutureClock ? 'success' : 'default'}
                        >
                          <AccessTimeIcon fontSize="small" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )
                })()}
                <Tooltip title="Mark as used" enterDelay={300}>
                  <span>
                    <IconButton
                      aria-label="Mark as used"
                      size="small"
                      onClick={() => void markUsedForMe(item).catch(() => undefined)}
                      disabled={isBusy}
                    >
                      <CheckCircleOutlineIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            )

            return (
              <Collapse key={item.id} timeout={200}>
                <ListItem
                  disablePadding
                  divider
                  sx={{
                    alignItems: 'center',
                    display: 'flex',
                    gap: 0.25,
                    px: 0,
                    minWidth: 0,
                  }}
                >
                  <ListItemButton
                    onClick={() => handleSelectItem(item)}
                    sx={{
                      flex: '1 1 auto',
                      minWidth: 0,
                      py: 0.85,
                      px: 0.25,
                      borderRadius: 1,
                    }}
                  >
                    {textContent}
                  </ListItemButton>
                  {actionsNode}
                </ListItem>
              </Collapse>
            )
          })}
        </TransitionGroup>
      </List>
    )
  }

  return (
    <>
      <DashboardCard
        title="My Prep Items"
        titleContent={headerTitle}
        page={page}
        perPage={perPage}
        total={total}
        collapsed={collapsed}
        onToggleCollapse={handleToggleCollapse}
        onChangePage={handleChangePage}
        loading={loading}
        refreshing={refreshing}
        onOpenSettings={handleOpenSettings}
        paperProps={{ sx: headerAccentSx }}
      >
        {content}
      </DashboardCard>
      <Snackbar
        open={Boolean(snackbar)}
        autoHideDuration={snackbar?.action ? 6000 : 4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        {snackbar ? (
          <Alert onClose={handleSnackbarClose} severity={snackbar.severity} action={snackbarAction} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        ) : null}
      </Snackbar>

      <AddPrepToShowClockModal
        open={Boolean(addToShowClockTarget)}
        stationId={stationId}
        prepItemId={addToShowClockTarget?.id ?? 0}
        prepHeadline={addToShowClockTarget?.headline ?? 'Prep item'}
        launchedFromMyPrep
        onClose={() => setAddToShowClockTarget(null)}
        onSuccessToast={(message) => setSnackbar({ message, severity: 'success' })}
      />
    </>
  )
}

export default MyPrepCard
